// PATH: SaberpDeals/frontend/src/pages/CatalogPage.tsx
import { useEffect, useState } from 'react';
import { catalogApi, type CatalogItem, type CatalogInput } from '@/lib/catalogApi';

const CATEGORIES = ['Package', 'License', 'Service'] as const;
const CURRENCIES = ['USD', 'IQD'] as const;

const catTag: Record<string, string> = {
    Package: 'bg-plum-soft text-plum',
    License: 'bg-forest-soft text-forest',
    Service: 'bg-amber2-soft text-amber2',
};

function money(n: number, cur: string) {
    const sym = cur === 'USD' ? '$' : 'IQD ';
    const dp = cur === 'USD' ? 2 : 0;
    return sym + n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

const emptyForm: CatalogInput = {
    code: '', name: '', category: 'Package', billingType: 'Recurring', defaultPrice: 0, currency: 'USD',
};

export default function CatalogPage() {
    const [items, setItems] = useState<CatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<CatalogItem | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState<CatalogInput>(emptyForm);
    const [saveErr, setSaveErr] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            setItems(await catalogApi.list());
            setError(null);
        } catch {
            setError('Failed to load catalog.');
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    function openAdd() {
        setEditing(null); setForm(emptyForm); setSaveErr(null); setShowForm(true);
    }
    function openEdit(i: CatalogItem) {
        setEditing(i);
        setForm({ code: i.code, name: i.name, category: i.category, billingType: i.billingType, defaultPrice: i.defaultPrice, currency: i.currency });
        setSaveErr(null); setShowForm(true);
    }

    async function save() {
        setSaving(true); setSaveErr(null);
        try {
            if (editing) await catalogApi.update(editing.id, form);
            else await catalogApi.create(form);
            setShowForm(false);
            await load();
        } catch (e: any) {
            setSaveErr(e?.response?.data?.error ?? 'Could not save.');
        } finally {
            setSaving(false);
        }
    }

    async function toggle(i: CatalogItem) {
        try { await catalogApi.toggle(i.id); await load(); } catch { /* ignore */ }
    }

    const grouped = CATEGORIES.map((c) => ({ cat: c, rows: items.filter((i) => i.category === c) }))
        .filter((g) => g.rows.length > 0);

    return (
        <div className="mt-6">
            <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-disp text-[21px] font-semibold tracking-tight">Catalog</div>
                    <div className="text-[13px] text-ink-3 mt-0.5 max-w-xl">
                        What you sell. Define once, reuse on every deal — prices pre-fill and stay editable.
                    </div>
                </div>
                <button onClick={openAdd} className="bg-forest text-paper text-[13px] font-semibold px-4 py-2.5 rounded-lg hover:bg-forest-2 transition">
                    + Add item
                </button>
            </div>

            {error && <div className="mb-4 rounded-lg bg-clay-soft border border-clay/30 px-4 py-3 text-sm text-clay">{error}</div>}

            {loading ? (
                <div className="text-ink-3 text-sm py-16 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-20 text-ink-3">
                    <div className="font-disp text-4xl mb-3 text-line-2">§</div>
                    <div className="font-disp text-lg font-semibold text-ink-2 mb-1.5">No items yet</div>
                    <div className="text-sm">Add your first license, package, or service.</div>
                </div>
            ) : (
                <div className="bg-card border border-line rounded-2xl overflow-hidden">
                    <div className="grid grid-cols-[1fr_116px_120px_120px_120px] gap-3 px-5 py-2.5 bg-paper border-b border-line text-[10.5px] font-semibold uppercase tracking-wide text-ink-3">
                        <span>Item</span><span>Category</span><span>Billing</span><span className="text-right">Default price</span><span className="text-right">Actions</span>
                    </div>
                    {grouped.map((g) => (
                        <div key={g.cat}>
                            <div className="font-mono text-[10px] font-semibold tracking-wider text-forest uppercase px-5 pt-4 pb-1.5">{g.cat}s</div>
                            {g.rows.map((i) => (
                                <div key={i.id} className={`grid grid-cols-[1fr_116px_120px_120px_120px] gap-3 items-center px-5 py-3 border-b border-paper-2 last:border-0 ${!i.isActive ? 'opacity-45' : ''}`}>
                                    <div>
                                        <div className="font-semibold text-sm">{i.name}</div>
                                        <div className="font-mono text-[11px] text-ink-3">{i.code}</div>
                                    </div>
                                    <span><span className={`text-[10.5px] font-mono font-semibold px-2.5 py-1 rounded-xl ${catTag[i.category]}`}>{i.category}</span></span>
                                    <span className="text-[12.5px] text-ink-2">{i.billingType === 'OneTime' ? 'One-time' : 'Recurring / yr'}</span>
                                    <span className="text-right font-mono font-semibold text-sm">{money(i.defaultPrice, i.currency)}</span>
                                    <span className="text-right text-[12.5px]">
                                        <button onClick={() => openEdit(i)} className="text-ink-3 hover:text-forest mr-3">Edit</button>
                                        <button onClick={() => toggle(i)} className="text-ink-3 hover:text-clay">{i.isActive ? 'Retire' : 'Restore'}</button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4" onClick={() => setShowForm(false)}>
                    <div className="bg-card rounded-2xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-disp text-lg font-semibold mb-4">{editing ? 'Edit item' : 'Add item'}</h3>
                        {saveErr && <div className="mb-3 rounded-lg bg-clay-soft border border-clay/30 px-3 py-2 text-[13px] text-clay">{saveErr}</div>}

                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Code</label>
                                <input className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="PKG-P2P" />
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Currency</label>
                                <select className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as any })}>
                                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                        </div>

                        <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Name</label>
                        <input className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 mb-3 outline-none focus:border-forest-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Procure-to-Pay" />

                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Category</label>
                                <select className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as any })}>
                                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Billing</label>
                                <select className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 outline-none focus:border-forest-2" value={form.billingType} onChange={(e) => setForm({ ...form, billingType: e.target.value as any })}>
                                    <option value="OneTime">One-time</option>
                                    <option value="Recurring">Recurring</option>
                                </select>
                            </div>
                        </div>

                        <label className="block text-[11px] font-semibold text-ink-2 uppercase tracking-wide mb-1.5">Default price</label>
                        <input type="number" min={0} step="any" className="w-full text-sm bg-card border border-line-2 rounded-lg px-3 py-2 mb-5 outline-none focus:border-forest-2" value={form.defaultPrice} onChange={(e) => setForm({ ...form, defaultPrice: parseFloat(e.target.value) || 0 })} />

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-ink-2 rounded-lg border border-line-2 hover:bg-paper">Cancel</button>
                            <button disabled={saving || !form.code || !form.name} onClick={save} className="px-4 py-2 text-sm font-semibold text-paper bg-forest rounded-lg hover:bg-forest-2 disabled:opacity-50">
                                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
